import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Zap, Coins, ArrowRight, ThumbsUp, ThumbsDown, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Bot } from '@/types/Bot';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

interface UnifiedAIAssistantProps {
  availableBots?: Bot[];
}

interface Recommendation {
  id: string;
  rank: number;
  fit_index: number;
  justification: string;
  pros?: string[];
  cons?: string[];
}

interface CreditDeductionResult {
  success: boolean;
  credits_remaining?: number;
  credits_needed?: number;
}

const SAMPLE_PROMPTS = [
  'AI for writing marketing emails on a tight budget',
  'Best free chatbot for coding help',
  'Tool to summarize long research papers',
  'AI assistant for customer support automation',
];

const STORAGE_KEY = 'vergo:ai-recommendations';

interface PersistedState {
  query: string;
  recommendations: Recommendation[];
}

const loadPersisted = (): PersistedState => {
  if (typeof window === 'undefined') return { query: '', recommendations: [] };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { query: '', recommendations: [] };
    const parsed = JSON.parse(raw);
    return {
      query: typeof parsed.query === 'string' ? parsed.query : '',
      recommendations: Array.isArray(parsed.recommendations) ? parsed.recommendations : [],
    };
  } catch {
    return { query: '', recommendations: [] };
  }
};

export const UnifiedAIAssistant: React.FC<UnifiedAIAssistantProps> = ({ availableBots = [] }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const initial = React.useMemo(loadPersisted, []);
  const [query, setQuery] = useState(initial.query);
  const [recommendations, setRecommendations] = useState<Recommendation[]>(initial.recommendations);
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);

  // Persist results across navigation (e.g. visiting a bot detail and coming back)
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ query, recommendations }));
    } catch {
      /* ignore quota errors */
    }
  }, [query, recommendations]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_credits')
      .select('credits_remaining')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => setCredits(data?.credits_remaining ?? 10));
  }, [user]);

  const deductCredits = async (amount: number) => {
    if (!user) return true;
    const { data, error } = await supabase.rpc('deduct_credits', {
      p_user_id: user.id,
      p_amount: amount,
      p_feature: 'ai_recommendations',
      p_description: 'AI recommendations',
    });
    if (error) return false;
    const result = data as unknown as CreditDeductionResult;
    if (!result.success) {
      toast({
        title: 'Not enough credits',
        description: `You need ${result.credits_needed} credits but have ${result.credits_remaining}.`,
        variant: 'destructive',
      });
      return false;
    }
    setCredits(result.credits_remaining);
    return true;
  };

  const handleRecommend = async (prompt?: string) => {
    const q = (prompt ?? query).trim();
    if (!q) {
      toast({ title: 'Describe what you need', variant: 'destructive' });
      return;
    }
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to use the AI assistant.',
        variant: 'destructive',
      });
      return;
    }
    if (availableBots.length === 0) {
      toast({ title: 'Catalog loading...', description: 'Please wait a moment.' });
      return;
    }

    const ok = await deductCredits(2);
    if (!ok) return;

    setIsLoading(true);
    setRecommendations([]);
    try {
      const { data, error } = await supabase.functions.invoke('vergo-ai', {
        body: {
          task_type: 'recommend',
          user_profile: q,
          catalog: availableBots.slice(0, 50).map((b) => ({
            id: b.id,
            name: b.name,
            description: b.description.substring(0, 200),
            category: b.category,
            tags: b.tags,
            features: b.features,
            price: b.price,
            rating: b.rating,
          })),
          top_k: 5,
        },
      });
      if (error) throw error;
      if (data?.error) {
        throw new Error(data.error.message || data.error || 'AI service returned an error');
      }
      setRecommendations(data.top_k || []);
    } catch (e: unknown) {
      toast({
        title: 'Recommendation failed',
        description: e instanceof Error ? e.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4 sm:space-y-6">
      {/* Header card */}
      <Card className="p-4 sm:p-6 bg-card border-border">
        <div className="flex items-start justify-between gap-3 mb-4 sm:mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-primary flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold leading-tight truncate">
                AI Recommendations
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Tell us your need — get the top 5 tools
              </p>
            </div>
          </div>
          {user && credits !== null && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-muted text-xs sm:text-sm shrink-0">
              <Coins className="w-3.5 h-3.5 text-primary" />
              <span className="font-semibold">{credits}</span>
            </div>
          )}
        </div>

        <Textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. AI for writing blog posts under $20/month"
          className="min-h-[90px] sm:min-h-[100px] text-sm sm:text-base resize-none mb-3"
          disabled={isLoading}
        />

        {user ? (
          <Button
            onClick={() => handleRecommend()}
            disabled={isLoading || !query.trim()}
            className="w-full h-11 text-sm sm:text-base"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Finding your matches...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Get Recommendations
                <span className="ml-2 text-xs opacity-75">2 credits</span>
              </>
            )}
          </Button>
        ) : (
          <Button asChild className="w-full h-11 text-sm sm:text-base">
            <Link to="/auth">
              <Sparkles className="w-4 h-4 mr-2" />
              Sign in to use AI Assistant
            </Link>
          </Button>
        )}

        {recommendations.length === 0 && !isLoading && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">Try one of these:</p>
            <div className="flex flex-wrap gap-1.5">
              {SAMPLE_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => setQuery(p)}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Results */}
      {recommendations.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground px-1">
            Top {recommendations.length} for you
          </h3>
          {recommendations.map((rec) => {
            const bot = availableBots.find((b) => b.id === rec.id);
            if (!bot) return null;
            return <RecommendationCard key={rec.id} rec={rec} bot={bot} />;
          })}
        </div>
      )}
    </div>
  );
};

const RecommendationCard: React.FC<{ rec: Recommendation; bot: Bot }> = ({ rec, bot }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="p-4 sm:p-5 bg-card border-border">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <Badge className="bg-primary text-primary-foreground shrink-0 h-7 px-2 text-xs font-bold">
          #{rec.rank}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/bot/${bot.id}`}
              className="font-semibold text-sm sm:text-base hover:text-primary transition-colors truncate"
            >
              {bot.name}
            </Link>
            <Badge variant="secondary" className="text-[10px] h-5 px-1.5 shrink-0">
              {rec.fit_index}% fit
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {bot.category} • {bot.price}
          </p>
        </div>
      </div>

      {/* Justification - always short */}
      <p className="text-sm text-foreground/90 leading-relaxed mb-3">
        {rec.justification}
      </p>

      {/* Pros/Cons - collapsible on mobile */}
      {(rec.pros?.length || rec.cons?.length) ? (
        <>
          {!expanded ? (
            <button
              onClick={() => setExpanded(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Show pros & cons
            </button>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {rec.pros && rec.pros.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600 dark:text-green-400">
                    <ThumbsUp className="w-3 h-3" /> Pros
                  </div>
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {rec.pros.slice(0, 3).map((p, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-green-600 dark:text-green-400">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {rec.cons && rec.cons.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400">
                    <ThumbsDown className="w-3 h-3" /> Cons
                  </div>
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {rec.cons.slice(0, 3).map((c, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-red-600 dark:text-red-400">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      ) : null}

      {/* CTA */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
        <Button asChild size="sm" variant="default" className="flex-1 h-9 text-xs sm:text-sm">
          <Link to={`/bot/${bot.id}`}>
            View details
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Link>
        </Button>
        {bot.website && (
          <Button asChild size="sm" variant="outline" className="h-9 text-xs sm:text-sm">
            <a href={bot.website} target="_blank" rel="noopener noreferrer">
              Visit
              <ExternalLink className="w-3 h-3 ml-1.5" />
            </a>
          </Button>
        )}
      </div>
    </Card>
  );
};

export default UnifiedAIAssistant;
